import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  X,
  Paintbrush,
  Sparkles,
  ShieldCheck,
  Download,
  FileText,
  Palette,
  Layers3,
  ArrowRight,
  Star,
  Quote,
  CheckCircle,
} from "lucide-react";
import QuotationModal from "./QuotationModal";
import QuotationDownloadModal from "./QuotationDownloadModal";

const CategoryData = [
  { id: 1, name: "LIME PLASTER", rate: 150 },
  { id: 2, name: "CONCRETE CONCEPT", rate: 150 },
  { id: 3, name: "MATT STUCCO", rate: 150 },
  { id: 4, name: "FENICE LUXURY", rate: 180 },
  { id: 5, name: "OTTOCENTO(ORO)", rate: 180 },
  { id: 6, name: "ENCANTO(FLOE)", rate: 180 },
  { id: 7, name: "OPECO MATT", rate: 180 },
  { id: 8, name: "STENCIL ART", rate: 180 },
  { id: 9, name: "CEMENTO", rate: 350 },
  { id: 10, name: "STONE EFFECT (Classic Effect)", rate: 450 },
  { id: 11, name: "STAMPED CONCRETE WALL", rate: 350 },
  { id: 12, name: "GRANITE", rate: 110 },
  { id: 13, name: "FLAKES", rate: 110 },
  { id: 14, name: "FINE FLAKES", rate: 110 },
  { id: 15, name: "NATURAL GRIT FINISH", rate: 110 },
  { id: 17, name: "SMOOTH STONE", rate: 130 },
  { id: 18, name: "STONE GRACE", rate: 130 },
  { id: 19, name: "NATURAL STONE", rate: 250 },
  { id: 20, name: "MUD FINISH", rate: 250 },
  { id: 21, name: "LIQUID METAL(METAL LINE)", rate: 450 },
];

const tabs = [
  { id: "overview", label: "Overview" },
  { id: "gallery", label: "Gallery" },
  { id: "catalogue", label: "Catalogue" },
  { id: "recommendation", label: "Recommendation" },
  { id: "downloads", label: "Downloads" },
];

const calculateDiscount = (sqft) => {
  const ranges = [
    { min: 1, max: 100, discount: 0 },
    { min: 100, max: 500, startDiscount: 0, endDiscount: 6.6 },
    { min: 500, max: 1000, startDiscount: 6.6, endDiscount: 13.3 },
    { min: 1000, max: 2000, startDiscount: 13.3, endDiscount: 20 },
    { min: 2000, max: 3000, startDiscount: 20, endDiscount: 23.3 },
    { min: 3000, max: 5000, startDiscount: 23.3, endDiscount: 26.6 },
    { min: 5000, max: 6000, startDiscount: 26.6, endDiscount: 33.3 },
    { min: 6000, max: 7000, startDiscount: 33.3, endDiscount: 36.6 },
    { min: 7000, max: 10000, startDiscount: 36.6, endDiscount: 40 },
    { min: 10000, max: Infinity, discount: 40 },
  ];

  const range = ranges.find((range) => sqft >= range.min && sqft <= range.max);
  if (range?.startDiscount !== undefined) {
    const ratio = (sqft - range.min) / (range.max - range.min);
    return (
      range.startDiscount + ratio * (range.endDiscount - range.startDiscount)
    );
  } else {
    return range?.discount;
  }
};

const ConcreteMattPage = () => {
  const { id, page, main_cat_id, CategoryId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [showQuotationDownloadModal, setShowQuotationDownloadModal] =
    useState(false);
  const [errors, setErrors] = useState({
    name: "",
    mobile: "",
    pincode: "",
    address: "",
    sqft: "",
  });
  const memoizedProducts = useMemo(() => products, [products]);

  const selectedCategoryName = CategoryData.find(
    (category) => category.id === parseInt(CategoryId),
  )?.name;

  const selectedProduct = memoizedProducts?.find(
    (product) => product.id === parseInt(id),
  );

  const existingCartItems = JSON.parse(localStorage.getItem("cartItems")) || [];

  const [formData, setFormData] = useState({
    name: existingCartItems[0]?.name || "",
    mobile: existingCartItems[0]?.mobile || "",
    address: existingCartItems[0]?.address || "",
    pincode: existingCartItems[0]?.pincode || "",
    selectedLocation: existingCartItems[0]?.selectedLocation || "",
    productName: selectedCategoryName || "",
    productsPattern: selectedProduct?.title || "",
    rate: "",
    sqft: "",
    selectedArea: "",
    amount: "",
    discount: 0,
    effectiveRate: 0,
    hsnSac: "995473",
    igst: 18,
  });

  useEffect(() => {
    if (selectedCategoryName || selectedProduct?.title) {
      setFormData((prev) => ({
        ...prev,
        productName: selectedCategoryName || prev.productName,
        productsPattern: selectedProduct?.title || prev.productsPattern,
      }));
    }
  }, [selectedCategoryName, selectedProduct]);

  useEffect(() => {
    if (formData?.sqft) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        sqft: formData.sqft ? "" : "Sq Ft is required",
      }));
    }
    if (formData?.selectedArea) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        selectedArea: formData.selectedArea ? "" : "Area is required",
      }));
    }
    if (formData?.mobile) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        mobile:
          formData.mobile.length === 10
            ? ""
            : "Mobile Number must be 10 digits",
      }));
    }
    if (formData?.pincode) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        pincode:
          formData.pincode.length === 6 ? "" : "Pincode must be 6 digits",
      }));
    }
    if (formData?.name) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        name: formData.name ? "" : "Name is required",
      }));
    }
    if (formData?.address) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        address: formData.address ? "" : "Address is required",
      }));
    }
    if (formData?.selectedLocation) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        selectedLocation: formData.selectedLocation
          ? ""
          : "Location is required",
      }));
    }
  }, [
    formData.name,
    formData.mobile,
    formData.pincode,
    formData.address,
    formData.sqft,
    formData.selectedLocation,
  ]);

  const handleOpenLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxPrev = () => {
    setLightboxIndex(
      (prev) => (prev - 1 + thumbnails.length) % thumbnails.length,
    );
  };

  const handleLightboxNext = () => {
    setLightboxIndex((prev) => (prev + 1) % thumbnails.length);
  };

  const [pincodeData, setPincodeData] = useState([]);

  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `https://webadmin.rigve.com/api/getproduct?main_cat_id=${main_cat_id}&cat_id=${CategoryId}&page=${page}`,
      );
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();
      setProducts(data.data?.data);
      setTotalPages(data.data?.last_page);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, id, main_cat_id]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const [mainImage, setMainImage] = useState("");
  useEffect(() => {
    setMainImage(selectedProduct?.image);
  }, [selectedProduct]);

  const thumbnails = [
    selectedProduct?.image,
    ...(selectedProduct?.gallery?.map((imgObj) => imgObj.img) || []),
  ].filter(Boolean);

  const handleThumbnailClick = (image) => {
    setMainImage(image);
  };

  const otherProducts = memoizedProducts
    ?.filter((product) => product.id !== parseInt(id))
    .slice(0, 3);

  const businessPhoneNumber = "+919427986568";
  const whatsappMessage = `Hello! I am interested in *${selectedProduct?.title}*.
Product: ${selectedProduct?.category}
Description: ${selectedProduct?.description}
Can you provide more details?`;

  const displayedThumbnails = showAll ? thumbnails : thumbnails.slice(0, 4);

  const handleWhatsAppClick = () => {
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${businessPhoneNumber}&text=${encodeURIComponent(
      whatsappMessage,
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  const fetchPincodeData = async (pincode) => {
    if (pincode.length === 6) {
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${pincode}`,
        );
        const data = await response.json();
        if (data[0].Status === "Success") {
          setPincodeData(data[0].PostOffice);
        } else {
          setPincodeData([]);
        }
      } catch (error) {
        console.error("Error fetching pincode data:", error);
        setPincodeData([]);
      }
    } else {
      setPincodeData([]);
    }
  };

  useEffect(() => {
    const selectedCategory = CategoryData.find(
      (category) => category.id === parseInt(CategoryId),
    );
    if (selectedCategory) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        rate: selectedCategory.rate.toString(),
      }));
    }
  }, [CategoryId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 10) {
        setFormData({ ...formData, [name]: numericValue });
      }
      return;
    }
    if (name === "pincode") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 6) {
        setFormData({ ...formData, [name]: numericValue });
        fetchPincodeData(numericValue);
      }
      return;
    }
    if (name === "sqft") {
      const numericValue = value.replace(/[^0-9.]/g, "");
      setFormData({ ...formData, [name]: numericValue });
      const sqftValue = parseFloat(numericValue) || 0;
      const discount = calculateDiscount(sqftValue);
      const rate = parseFloat(formData.rate) || 0;
      const effectiveRate = rate * (1 - discount / 100);
      const amount = effectiveRate * sqftValue;
      const igst = parseFloat(formData.igst) || 0;
      const amountWithIgst = amount * (1 + igst / 100);
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
        discount: discount?.toFixed(2),
        effectiveRate: effectiveRate?.toFixed(2),
        amount: amount?.toFixed(2),
        amountWithIgst: amountWithIgst?.toFixed(2),
      }));
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const [isLoadingWhatsAppQuote, setIsLoadingWhatsAppQuote] = useState(false);

  const handleAddToCart = () => {
    setErrors({
      name: "",
      mobile: "",
      pincode: "",
      address: "",
      sqft: "",
      selectedArea: "",
    });

    let isValid = true;
    const newErrors = { ...errors };

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile Number is required";
      isValid = false;
    } else if (formData.mobile.length !== 10) {
      newErrors.mobile = "Mobile Number must be 10 digits";
      isValid = false;
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
      isValid = false;
    } else if (formData.pincode.length !== 6) {
      newErrors.pincode = "Pincode must be 6 digits";
      isValid = false;
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
      isValid = false;
    }

    if (!formData.sqft.trim()) {
      newErrors.sqft = "Sq Ft is required";
      isValid = false;
    }

    if (!formData.selectedArea.trim()) {
      newErrors.selectedArea = "Area is required";
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      return;
    }

    const cartItem = {
      name: formData.name,
      mobile: formData.mobile,
      address: formData.address,
      pincode: formData.pincode,
      selectedLocation: formData.selectedLocation,
      productName: selectedCategoryName || formData.productName,
      productsPattern: selectedProduct?.title,
      rate: formData.rate,
      Area: formData.selectedArea,
      sqft: formData.sqft,
      discount: formData.discount,
      effectiveRate: formData.effectiveRate,
      amount: formData.amount,
      pageUrl: window.location.href,
      mainImage: mainImage,
      amountWithIgst: formData.amountWithIgst,
    };

    const existingCartItems =
      JSON.parse(localStorage.getItem("cartItems")) || [];

    const updatedCartItems = [...existingCartItems, cartItem];
    localStorage.setItem("cartItems", JSON.stringify(updatedCartItems));

    setShowQuotationModal(false);
    window.location.reload();
  };

  const handleWhatsAppQuote = async () => {
    setErrors({
      name: "",
      mobile: "",
      pincode: "",
      address: "",
      sqft: "",
      selectedArea: "",
    });

    let isValid = true;
    const newErrors = { ...errors };

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile Number is required";
      isValid = false;
    } else if (formData.mobile.length !== 10) {
      newErrors.mobile = "Mobile Number must be 10 digits";
      isValid = false;
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
      isValid = false;
    } else if (formData.pincode.length !== 6) {
      newErrors.pincode = "Pincode must be 6 digits";
      isValid = false;
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
      isValid = false;
    }

    if (!formData.sqft.trim()) {
      newErrors.sqft = "Sq Ft is required";
      isValid = false;
    }

    if (!formData.selectedArea.trim()) {
      newErrors.selectedArea = "Area is required";
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      return;
    }

    const message = `Hello! I am interested in a quote for the following details:
  Name: ${formData.name}
  Mobile: ${formData.mobile}
  Address: ${formData.address}
  Pincode: ${formData.pincode}
  Location: ${formData.selectedLocation}
  Category: ${selectedCategoryName || "N/A"}
  Product: ${selectedProduct?.title || formData.productName}
  Area: ${formData.selectedArea}
  Rate: ${formData.rate}
  Discount: ${formData.discount + "%"}
  Discount Rate: ${formData?.effectiveRate}
  Sq Ft: ${formData.sqft}
  Amount: ${formData.amount}
  HSN/SAC: ${formData.hsnSac}
  IGST: ${formData.igst + "%"}
  Amount with IGST: ${formData.amountWithIgst}`;
    setIsLoadingWhatsAppQuote(true);
    try {
      const response = await fetch(
        `https://webadmin.rigve.com/api/save_contact?name=${encodeURIComponent(
          formData.name,
        )}&phone=${encodeURIComponent(
          formData.mobile,
        )}&email=${encodeURIComponent(
          formData.email || "-",
        )}&msg=${encodeURIComponent(message)}`,
      );
      if (!response.ok) {
        throw new Error("Failed to save contact");
      }
      setTimeout(() => {
        const whatsappUrl = `https://api.whatsapp.com/send?phone=${businessPhoneNumber}&text=${encodeURIComponent(
          `New form submission: ${message}`,
        )}`;

        window.open(whatsappUrl, "_blank");
        setIsLoadingWhatsAppQuote(false);
        setShowQuotationModal(false);
      }, 500);
    } catch (error) {
      console.error("Error saving contact:", error);
      setIsLoadingWhatsAppQuote(false);
      setShowQuotationModal(false);
      alert("Failed to send WhatsApp message. Please try again later.");
    }
  };

  const [activeTab, setActiveTab] = useState("overview");

  const handleScroll = (id) => {
    setActiveTab(id);
    const section = document.getElementById(id);
    if (section) {
      const offset = 120;
      const top =
        section.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({
        top,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const handleActiveSection = () => {
      const scrollPosition = window.scrollY + 150;
      tabs.forEach((tab) => {
        const section = document.getElementById(tab.id);
        if (section) {
          const top = section.offsetTop;
          const height = section.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveTab(tab.id);
          }
        }
      });
    };

    window.addEventListener("scroll", handleActiveSection);
    return () => {
      window.removeEventListener("scroll", handleActiveSection);
    };
  }, []);

  useEffect(() => {
    if (showQuotationModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showQuotationModal]);

  return (
    <>
      <div className="w-full">
        <Helmet>
          <title>{`${selectedProduct?.title} - Professional Products Pattern`}</title>
          <meta
            name="description"
            content={`Discover ${selectedProduct?.title}, a professional product pattern in the category of ${selectedProduct?.category}. Learn more about its features and benefits.`}
          />
          <meta
            name="keywords"
            content={`${selectedProduct?.title}, professional products, ${selectedProduct?.category}, product details`}
          />
          <meta
            property="og:title"
            content={`${selectedProduct?.title} - Professional Products Pattern`}
          />
          <meta
            property="og:description"
            content={`Discover ${selectedProduct?.title}, a professional product pattern in the category of ${selectedProduct?.category}. Learn more about its features and benefits.`}
          />
          <meta property="og:image" content={selectedProduct?.image} />
          <meta property="og:url" content={window.location.href} />
        </Helmet>

        {/* Product Showcase */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#111827] to-[#1e293b] text-white py-16">
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/asfalt-dark.png')]"></div>
          <div className="absolute -top-24 -left-24 w-72 h-72 bg-orange-500/20 blur-3xl rounded-full"></div>
          <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-cyan-500/20 blur-3xl rounded-full"></div>

          <div className="relative container mx-auto px-4 max-w-7xl">
            <div className="text-center max-w-4xl mx-auto mb-14">
              <span className="inline-block px-4 py-2 rounded-full border border-orange-400/40 bg-orange-500/10 text-orange-300 text-xs tracking-widest uppercase">
                Luxury Wall Texture Collection
              </span>

              <h1 className="mt-5 text-2xl font-bold leading-tight">
                Featured Texture:{" "}
                <span className="text-orange-400">
                  {selectedProduct?.title}
                </span>
              </h1>
            </div>

            <div className="grid lg:grid-cols-2 gap-10 items-start">
              <div>
                <div
                  onClick={() =>
                    handleOpenLightbox(thumbnails.indexOf(mainImage))
                  }
                  className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl cursor-zoom-in"
                >
                  <img
                    src={mainImage}
                    alt={selectedProduct?.title}
                    className="w-full h-[280px] md:h-[420px] object-cover hover:scale-105 transition duration-500"
                  />
                </div>

                <div className="grid grid-cols-4 gap-3 mt-5">
                  {displayedThumbnails.map((thumb, index) => (
                    <div
                      key={index}
                      onClick={() => handleThumbnailClick(thumb)}
                      className="relative cursor-pointer rounded-xl overflow-hidden border border-white/10 hover:border-orange-400 transition"
                    >
                      <img
                        src={thumb}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-20 object-cover hover:scale-110 transition duration-300"
                      />

                      {!showAll && index === 3 && thumbnails.length > 4 && (
                        <div
                          className="absolute inset-0 bg-black/70 flex items-center justify-center text-sm font-semibold"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAll(true);
                          }}
                        >
                          +{thumbnails.length - 4} More
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {lightboxOpen && (
                  <div
                    className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setLightboxOpen(false)}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLightboxPrev();
                      }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl z-10"
                    >
                      ‹
                    </button>

                    <img
                      src={thumbnails[lightboxIndex]}
                      alt={`Zoom ${lightboxIndex + 1}`}
                      onClick={(e) => e.stopPropagation()}
                      className="max-h-[90vh] max-w-[90vw] rounded-2xl shadow-2xl object-contain"
                    />

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLightboxNext();
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl z-10"
                    >
                      ›
                    </button>

                    <div className="absolute bottom-6 text-white/70 text-sm">
                      {lightboxIndex + 1} / {thumbnails.length}
                    </div>

                    <button
                      onClick={() => setLightboxOpen(false)}
                      className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 text-white rounded-full w-10 h-10 flex items-center justify-center"
                    >
                      <X size={18} />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 backdrop-blur-md shadow-xl">
                  <h2 className="text-2xl font-bold text-white">
                    {selectedProduct?.title}
                  </h2>

                  <p className="mt-3 text-md text-gray-300">
                    Product:{" "}
                    <span className="text-orange-400 font-semibold">
                      {selectedProduct?.category}
                    </span>
                  </p>

                  <p className="mt-5 text-md text-gray-300 leading-relaxed">
                    {selectedProduct?.description ||
                      "Elegant decorative texture crafted with superior quality materials to create stylish, luxurious and modern wall finishes for all spaces."}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4 mt-8">
                    <button
                      onClick={() => setShowQuotationModal(true)}
                      className="w-full py-4 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-lg transition"
                    >
                      Get Custom Quote
                    </button>

                    <button
                      onClick={handleWhatsAppClick}
                      className="w-full py-4 rounded-full bg-green-500 hover:bg-green-600 text-white font-semibold shadow-lg transition"
                    >
                      WhatsApp Inquiry
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Products Section (Shows ONLY if otherProducts is not empty) */}
        {otherProducts && otherProducts.length > 0 && (
          <section className="relative bg-gradient-to-b from-white via-orange-50 to-white py-16 overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute top-0 left-0 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-30"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-40"></div>
            <div className="container mx-auto px-4 max-w-7xl relative z-10">
              {/* Heading */}
              <div className="text-center mb-14">
                <span className="text-orange-500 uppercase tracking-[4px] text-sm font-semibold">
                  Explore More
                </span>
                <h2 className="mt-3 text-xl lg:text-2xl font-bold text-gray-900">
                  Related Wall Texture Patterns
                </h2>
                <p className="mt-4 text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
                  Discover premium wall texture designs crafted to transform
                  your walls into artistic masterpieces.
                </p>
              </div>

              {/* Product Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {otherProducts.map((product) => (
                  <div
                    key={product.id}
                    className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100"
                  >
                    {/* Image Section */}
                    <div className="relative overflow-hidden">
                      <img
                        src={product.image || "/images/wall-texture.jpg"}
                        alt={product.title}
                        loading="lazy"
                        className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                      />

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-500"></div>

                      <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition duration-500">
                        <a
                          href={`/product/${product.id}/${page}/${main_cat_id}/${CategoryId}/${encodeURIComponent(product.title)}`}
                          className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg hover:bg-orange-600"
                        >
                          View Product →
                        </a>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-md font-bold text-gray-900 group-hover:text-orange-500 transition">
                        {product.title}
                      </h3>
                      <p className="mt-3 text-gray-600 text-sm leading-relaxed line-clamp-3">
                        {product.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Button */}
              <div className="mt-14 text-center">
                <a
                  href={`/collections/${CategoryId}/${main_cat_id}`}
                  className="inline-flex items-center gap-2 bg-orange-500 text-white px-8 py-3 rounded-full font-semibold shadow-md hover:bg-orange-600 hover:scale-105 transition duration-300"
                >
                  See All Patterns
                  <span>→</span>
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Modal */}
        {showQuotationModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-3">
            <div className="relative w-full max-w-6xl max-h-[95vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-orange-100">
              <div className="absolute top-0 left-0 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-20"></div>
              <div className="absolute bottom-0 right-0 w-80 h-80 bg-yellow-100 rounded-full blur-3xl opacity-30"></div>

              <div className="sticky top-0 z-20 flex items-center justify-between px-8 py-5 bg-white/90 backdrop-blur-lg border-b border-gray-100 rounded-t-3xl">
                <div>
                  <p className="text-sm uppercase tracking-[3px] text-orange-500 font-semibold">
                    Wall Texture Quote
                  </p>
                  <h3 className="text-xl font-bold text-gray-900">
                    Premium Quotation Form
                  </h3>
                </div>

                <button
                  onClick={() => setShowQuotationModal(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-500 transition"
                >
                  <X size={22} />
                </button>
              </div>

              <div className="relative z-10 p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="hidden lg:block bg-gradient-to-br from-orange-500 to-orange-400 rounded-2xl p-5 text-white relative overflow-hidden">
                  <img
                    src={mainImage}
                    alt="Wall Texture"
                    className="rounded-2xl shadow-lg mb-6 h-64 w-full object-cover"
                  />

                  <h3 className="text-xl font-bold mb-3">
                    Design Your Dream Wall
                  </h3>

                  <p className="text-sm leading-relaxed text-orange-100">
                    Fill your details and get an instant professional quotation
                    for premium wall textures and elegant finishes.
                  </p>

                  <div className="mt-8 space-y-3">
                    <div className="bg-white/10 p-3 rounded-xl">
                      ✔ Premium Materials
                    </div>
                    <div className="bg-white/10 p-3 rounded-xl">
                      ✔ Best Price Guarantee
                    </div>
                    <div className="bg-white/10 p-3 rounded-xl">
                      ✔ Fast Installation
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 shadow-sm">
                    <h3 className="text-xl font-bold text-gray-900 mb-5">
                      Customer Details
                    </h3>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Full Name"
                          className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 outline-none"
                        />
                        {errors.name && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Mobile Number
                        </label>
                        <input
                          type="text"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleInputChange}
                          placeholder="Mobile Number"
                          maxLength="10"
                          className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 outline-none"
                        />
                        {errors.mobile && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.mobile}
                          </p>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Full Address
                        </label>
                        <textarea
                          name="address"
                          rows="3"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Full Address"
                          className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 outline-none"
                        />
                        {errors.address && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.address}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Pincode
                        </label>
                        <input
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          placeholder="Pincode"
                          maxLength="6"
                          className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 outline-none"
                        />
                        {errors.pincode && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.pincode}
                          </p>
                        )}
                      </div>

                      {formData.pincode.length === 6 &&
                        pincodeData.length > 0 && (
                          <div>
                            <label className="block mb-2 text-sm font-semibold text-gray-700">
                              Select Location
                            </label>
                            <select
                              name="selectedLocation"
                              value={formData.selectedLocation}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 outline-none"
                            >
                              <option value="">Select Location</option>
                              {pincodeData.map((location, index) => (
                                <option key={index} value={location.Name}>
                                  {location.Name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 shadow-sm">
                    <h3 className="text-xl font-bold text-gray-900 mb-5">
                      Product Information
                    </h3>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Category
                        </label>
                        <input
                          type="text"
                          value={selectedCategoryName || ""}
                          readOnly
                          placeholder="Category"
                          className="w-full px-4 py-3 text-sm rounded-xl bg-white border border-gray-300"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Product Title
                        </label>
                        <input
                          type="text"
                          value={selectedProduct?.title || ""}
                          readOnly
                          placeholder="Product Title"
                          className="w-full px-4 py-3 text-sm rounded-xl bg-white border border-gray-300"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Rate
                        </label>
                        <input
                          type="text"
                          value={formData.rate}
                          readOnly
                          placeholder="Rate"
                          className="w-full px-4 py-3 text-sm rounded-xl bg-white border border-gray-300"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Sq Ft
                        </label>
                        <input
                          type="text"
                          name="sqft"
                          value={formData.sqft}
                          onChange={handleInputChange}
                          placeholder="Enter Sq Ft"
                          className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 outline-none"
                        />
                        {errors.sqft && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.sqft}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Select Area
                        </label>
                        <select
                          name="selectedArea"
                          value={formData.selectedArea}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-orange-400 outline-none"
                        >
                          <option value="">Select Area</option>
                          <option value="Interior Wall">Interior Wall</option>
                          <option value="Exterior Wall">Exterior Wall</option>
                          <option value="Ceiling Wall">Ceiling Wall</option>
                          <option value="Flooring">Flooring</option>
                        </select>
                        {errors.selectedArea && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.selectedArea}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Discount
                        </label>
                        <input
                          type="text"
                          value={formData.discount ? formData.discount : "0%"}
                          readOnly
                          placeholder="Discount"
                          className="w-full px-4 py-3 text-sm rounded-xl bg-white border border-gray-300"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Amount
                        </label>
                        <input
                          type="text"
                          value={formData.amount}
                          readOnly
                          placeholder="Amount"
                          className="w-full px-4 py-3 text-sm rounded-xl bg-white border border-gray-300"
                        />
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                          Amount with GST
                        </label>
                        <input
                          type="text"
                          value={formData.amountWithIgst}
                          readOnly
                          placeholder="Amount with GST"
                          className="w-full px-4 py-3 text-sm rounded-xl bg-white border border-gray-300"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <button
                      onClick={() => setShowQuotationModal(false)}
                      className="w-full px-6 py-3 text-sm rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      Close
                    </button>

                    <button
                      onClick={handleAddToCart}
                      className="w-full px-6 py-3 text-sm rounded-xl bg-blue-600 text-white hover:bg-blue-700 shadow-lg"
                    >
                      Add To Cart
                    </button>

                    <button
                      onClick={() => setShowQuotationDownloadModal(true)}
                      className="w-full px-4 py-3 text-sm rounded-xl bg-purple-600 text-white hover:bg-purple-700 shadow-lg"
                    >
                      Download Quote
                    </button>

                    <button
                      onClick={handleWhatsAppQuote}
                      disabled={isLoadingWhatsAppQuote}
                      className="w-full px-4 py-3 text-sm rounded-xl bg-green-500 text-white hover:bg-green-600 shadow-lg"
                    >
                      {isLoadingWhatsAppQuote
                        ? "Processing..."
                        : "WhatsApp Quote"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showQuotationDownloadModal && (
        <QuotationDownloadModal
          cartItem={formData}
          selectedProduct={selectedProduct}
          onClose={() => setShowQuotationDownloadModal(false)}
        />
      )}
    </>
  );
};

export default ConcreteMattPage;
